import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import ScrollFadeIn from "../components/ScrollFadeIn";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";
import RelatedPosts from "../components/RelatedPosts";
import {
  IconArrowLeft,
  IconCalendar,
  IconClock,
  IconChevronRight,
} from "@tabler/icons-react";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`/api/posts/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Post not found");
          }
          throw new Error("Failed to fetch post");
        }
        const data = await response.json();
        setPost(data);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]+>/g, "");
    const wordCount = text.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime;
  };

  const stripHtml = (html) => {
    return html?.replace(/<[^>]+>/g, "").trim() || "";
  };

  const getMetaDescription = (excerpt, maxLength = 160) => {
    const text = stripHtml(excerpt);
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3).trim() + "...";
  };

  const slugToTitle = (value) => {
    if (!value) return "Blog Post";
    return value
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const fallbackTitle = slugToTitle(slug);
  const fallbackDescription =
    "Explore engineering insights, precision manufacturing, industrial technology, and practical solutions from GXL Engineering experts.";

  if (loading) {
    return (
      <>
        <SEO
          title={fallbackTitle}
          description={fallbackDescription}
          url={`/blog/${slug}`}
          type="article"
          noSuffix
        />
        <div className="bg-[#0B0F11]">
          <Header />
        </div>
        <div className="bg-[#E5E5E6] min-h-screen flex justify-center items-center">
          <div className="loader">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO
          title={fallbackTitle}
          description={fallbackDescription}
          url={`/blog/${slug}`}
          type="article"
          noSuffix
        />
        <div className="bg-[#0B0F11]">
          <Header />
        </div>
        <div className="bg-[#E5E5E6] min-h-screen flex flex-col justify-center items-center py-20">
          <h1 className="text-3xl font-semibold text-gray-900 mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 text-[#C17C56] font-medium hover:gap-3 transition-all duration-300">
            <IconArrowLeft size={20} />
            Back to Blog
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={post.seo?.title || stripHtml(post.title.rendered)}
        description={post.seo?.description || getMetaDescription(post.excerpt.rendered)}
        image={post.seo?.ogImage || post.featuredImage}
        url={`/blog/${post.slug}`}
        type="article"
        noSuffix
        article={{
          publishedTime: post.date,
          modifiedTime: post.modified,
        }}
      />
      <div className="bg-[#0B0F11]">
        <Header />
      </div>

      {/* Hero Section */}
      <section className="bg-[#0B0F11] lg:pt-20 pt-10 lg:pb-16 pb-10 w-full">
        <div className="w-full max-w-[1024px] mx-auto lg:px-10 px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[rgb(171,174,187)] hover:text-white mb-8 transition-colors duration-300">
            <IconArrowLeft size={20} />
            Back to Blog
          </Link>
          <div className="flex items-center gap-4 text-[rgb(171,174,187)] text-sm mb-4">
            <span className="flex items-center gap-1">
              <IconCalendar size={16} stroke={1.5} />
              {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1">
              <IconClock size={16} stroke={1.5} />
              {getReadingTime(post.content.rendered)} min read
            </span>
          </div>
          <h1
            className="text-white lg:text-4xl text-2xl font-semibold mb-4"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />
          <div
            className="text-[rgb(171,174,187)] text-lg mb-6"
            dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
          />

          {/* Share Buttons - Hero */}
          <ShareButtons
            url={`/blog/${post.slug}`}
            title={stripHtml(post.title.rendered)}
          />

          {/* Featured Image */}
          {post.featuredImage && (
            <div className="mt-10 rounded-2xl overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title.rendered.replace(/<[^>]+>/g, "")}
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#E5E5E6] lg:py-20 py-10 w-full">
        <div className="w-full max-w-[1024px] mx-auto lg:px-10 px-6">
          {post.content?.rendered ? (
            <article
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            />
          ) : (
            <p className="text-gray-600 text-center py-10">
              No content available.
            </p>
          )}

          {/* Share Buttons - End of Article */}
          <div className="mt-12 pt-8 border-t border-gray-300">
            <p className="text-gray-700 font-medium mb-4">
              Found this article helpful? Share it with your network.
            </p>
            <ShareButtons
              url={`/blog/${post.slug}`}
              title={stripHtml(post.title.rendered)}
              variant="light"
            />
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <RelatedPosts currentSlug={slug} />

      {/* CTA Section */}
      <section className="bg-[#0B0F11] lg:py-20 py-10">
        <div className="container lg:px-10 px-6 max-w-[1600px] mx-auto">
          <ScrollFadeIn>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-white lg:text-3xl text-2xl font-semibold mb-4">
                Ready to discuss your project?
              </h2>
              <p className="text-[rgb(171,174,187)] mb-8">
                Our experienced engineers are here to help turn your ideas into
                precision-engineered reality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="transition-colors duration-300 group inline-flex items-center outline-none justify-center tracking-tight leading-none focus:outline-white focus:outline-1 focus:outline-offset-4 h-12 text-[15px] px-8 rounded-full shadow-[0px_2px_2px_0px_rgba(0,0,0,0.74)] font-semibold relative">
                  <span className="absolute -inset-px bottom-[-1.5px] rounded-full bg-[linear-gradient(180deg,#fcc171_0%,#C17C56_50%,#362821_100%)]"></span>
                  <span className="absolute -top-[5px] bottom-0.5 left-1/2 w-[91%] -translate-x-1/2 bg-btn-glowing mix-blend-screen blur-[1px] transition-transform duration-300 ease-in-out group-hover:translate-y-[-2px]"></span>
                  <span className="absolute inset-0 rounded-full bg-black"></span>
                  <span className="absolute inset-0 rounded-full bg-btn-glowing-inset opacity-100 transition-opacity duration-300 ease-in-out group-hover:opacity-0"></span>
                  <span className="absolute inset-0 rounded-full bg-btn-glowing-inset-hover opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></span>
                  <span className="relative z-20 bg-[linear-gradient(180deg,#FFF_33.33%,#E4D0B1_116.67%)] bg-clip-text text-transparent">
                    Get in Touch
                  </span>
                </a>
                <Link
                  to="/blog"
                  className="group text-base font-medium text-white flex gap-1 items-center justify-center">
                  More Articles
                  <IconChevronRight
                    size={20}
                    stroke={2}
                    className="chevron-icon"
                  />
                </Link>
              </div>
            </div>
          </ScrollFadeIn>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
